/*  BankingTab.tsx  (drop-in replacement with search by USERNAME & ROLE)  */
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Snackbar, Alert, AlertColor } from "@mui/material";
import { apiFetch, BASE } from "../../utils/api";
import axios from "axios";

/* ─────────────────────────────────────────── CONSTANTS */
export const ROLE_HIERARCHY = {
  SuperAdmin: { level: 0, color: "bg-black", description: "Supreme admin" },
  Admin: { level: 1, color: "bg-red-500", description: "Admin" },
  SubAdmin: { level: 2, color: "bg-orange-500", description: "Sub-Admin" },
  Master: { level: 3, color: "bg-blue-500", description: "Master" },
  Agent: { level: 4, color: "bg-green-500", description: "Agent" },
  SubAgent: { level: 5, color: "bg-purple-500", description: "Sub-Agent" },
  User: { level: 6, color: "bg-gray-500", description: "Player" },
} as const;

/* ─────────────────────────────────────────── TYPES */
export interface Row {
  id: number;
  name: string; // username (player) or full name
  role: keyof typeof ROLE_HIERARCHY;
  balance: number;
  parent_id: number | null;
  phone?: string | null;
  country?: string | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

/* ─────────────────────────────────────────── HELPERS */
const toArray = (x: any) =>
  Array.isArray(x) ? x : Array.isArray(x.data) ? x.data : [];

const BalanceCard: React.FC<{
  title: string;
  balance: number;
  currency?: string;
}> = ({ title, balance, currency = "INR" }) => (
  <div className="bg-[#232626] p-3 rounded-lg text-white text-center border border-[#2f3536] shadow-sm flex flex-col justify-center">
    <h4 className="text-sm md:text-md font-semibold mb-2 text-gray-300">
      {title}
    </h4>
    <div className="bg-[#25ee89] rounded-full px-2 py-2">
      <p className="text-black font-bold text-xs md:text-sm">{`${currency} (${balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`}</p>
    </div>
  </div>
);

/* ─────────────────────────────────────────── COMPONENT */
export default function BankingTab() {
  /* who am I? */
  const myRole =
    (localStorage.getItem("userRole") as keyof typeof ROLE_HIERARCHY) ||
    "SuperAdmin";
  const myId = Number(localStorage.getItem("currentUserId") || 1);
  const myLvl = ROLE_HIERARCHY[myRole].level;

  /* state */
  const [rows, setRows] = useState<Row[]>([]);
  const [rollup, setRollup] = useState<Record<number, any>>({});
  const [myBal, setMyBal] = useState(0);
  const [depVal, setDepVal] = useState<Record<number, string>>({});
  const [wdVal, setWdVal] = useState<Record<number, string>>({});
  const [snack, setSnack] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(true);
  const [plData, setPlData] = useState<Record<number, number>>({});
  const [plLoading, setPlLoading] = useState<Record<number, boolean>>({});
  const [exposureData, setExposureData] = useState<Record<number, number>>({});
  const [totalPlatformExposure, setTotalPlatformExposure] = useState<number>(0);

  // Exposure modal state
  const [expModal, setExpModal] = useState(false);
  const [expModalUser, setExpModalUser] = useState<{id: number, name: string} | null>(null);
  const [expModalBets, setExpModalBets] = useState<any[]>([]);
  const [expModalLoading, setExpModalLoading] = useState(false);

  // search state (new)
  const [q, setQ] = useState(""); // free-text search for USERNAME
  const [roleFilter, setRoleFilter] = useState<string>("All"); // role dropdown

  /* fetch -------------------------------------------------------------------------------- */
  const openExposureModal = async (user: {id: number, name: string}) => {
    setExpModalUser(user);
    setExpModal(true);
    setExpModalLoading(true);
    setExpModalBets([]);
    try {
      const token = localStorage.getItem("token");
      const staffId = localStorage.getItem("currentUserId");
      const headers: Record<string, string> = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(staffId ? { "x-staff-id": staffId } : {})
      };

      // Fetch open bets for this user AND their downline users
      // First try fetching the user's own bets
      const ownRes = await axios.get(`${BASE}/betHistory/transactions/user/${user.id}?page=1&limit=100`, { headers }).catch(() => ({ data: { transactions: [] } }));
      
      // Also fetch bets for downline users (staff's users exposure)
      const downlineRes = await axios.get(`${BASE}/sportsbetting/exposure-details/${user.id}`, { headers }).catch(() => ({ data: { bets: [] } }));

      // Process own bets - group by round and find open ones
      const txns = ownRes.data.transactions || [];
      const roundGroups: Record<string, any[]> = {};
      const noRound: any[] = [];
      txns.forEach((bet: any) => {
        if (bet.round_id) {
          if (!roundGroups[bet.round_id]) roundGroups[bet.round_id] = [];
          roundGroups[bet.round_id].push(bet);
        } else {
          noRound.push(bet);
        }
      });

      const merged: any[] = [];
      Object.values(roundGroups).forEach((group: any[]) => {
        const betTx = group.find((t: any) => t.transaction_type === 'BET');
        const winTx = group.find((t: any) => t.transaction_type === 'WIN');
        const lossTx = group.find((t: any) => t.transaction_type === 'LOSS');
        const resultTx = winTx || lossTx;
        const baseTx = betTx || resultTx || group[0];
        merged.push({
          ...baseTx,
          transaction_type: resultTx ? resultTx.transaction_type : 'BET',
          amount: betTx ? betTx.amount : (resultTx?.amount || 0),
          result_amount: resultTx ? resultTx.amount : null,
        });
      });
      noRound.forEach((bet: any) => merged.push(bet));
      const ownOpenBets = merged.filter((b: any) => b.transaction_type === 'BET');

      // Process downline bets from exposure-details endpoint
      const downlineBets = (downlineRes.data.bets || []).map((b: any) => ({
        ...b,
        transaction_type: 'BET',
        transaction_timestamp: b.created_at || b.transaction_timestamp,
      }));

      // Combine both sources and deduplicate by id
      const seen = new Set<number>();
      const allBets: any[] = [];
      
      // Prefer downline bets (they have better match details)
      for (const bet of downlineBets) {
        if (!seen.has(bet.id)) {
          seen.add(bet.id);
          allBets.push(bet);
        }
      }
      for (const bet of ownOpenBets) {
        if (!seen.has(bet.id)) {
          seen.add(bet.id);
          allBets.push(bet);
        }
      }
      
      setExpModalBets(allBets.sort((a: any, b: any) => new Date(b.transaction_timestamp || 0).getTime() - new Date(a.transaction_timestamp || 0).getTime()));
    } catch (e) {
      console.error("Failed to fetch exposure bets:", e);
    } finally {
      setExpModalLoading(false);
    }
  };

  const refresh = async () => {
    try {
      setLoading(true);
      const [list, rollAll, self] = await Promise.all([
        apiFetch<Row[]>("/api/staff"),
        apiFetch<any[]>(`/api/staff/rollupStaff/${myId}`),
        apiFetch<any>(`/api/staff/rollup/${myId}`),
      ]);

      setRows(toArray(list));
      const rows1 = toArray(list);
      setMyBal(Number(self.data?.own_balance || 0));
      const arr = toArray(rollAll); // make sure it’s an array
      const map = Object.fromEntries(
        arr.map((o: any) => [Number(o.staff_id), o]),
      );
      setRollup(map);
      // Fetch P/L for each row
      // Skip P/L fetching if rows is empty
      if (rows1.length === 0) {
        console.warn("[refresh] No rows to fetch P/L for");
        setPlData({});
        setPlLoading({});
        return;
      }

      // Fetch P/L for each row
      const plPromises = await rows1.map(
        async (row: { id: any; role: string }) => {
          setPlLoading((prev) => ({ ...prev, [Number(row.id)]: true }));
          const endpoint =
            row.role === "User"
              ? "/api/deposit/user-pl"
              : "/api/deposit/staff-pl";
          const body =
            row.role === "User" ? { userId: row.id } : { staffId: row.id };
          console.log(
            `[refresh] Fetching P/L for ${row.role} ${row.id} at ${endpoint} with body:`,
            body,
          ); // Debug: Log API call
          try {
            const response = await apiFetch(endpoint, {
              method: "POST",
              body: JSON.stringify(body),
            });
            console.log(
              `[refresh] P/L response for ${row.role} ${row.id}:`,
              response,
            ); // Debug: Log response
            const profitLoss = Number(response?.data?.profitLoss ?? 0);
            if (isNaN(profitLoss)) {
              console.warn(
                `[refresh] Invalid profitLoss for ${row.role} ${row.id}:`,
                response,
              );
              return { id: Number(row.id), profitLoss: 0 };
            }
            return { id: Number(row.id), profitLoss };
          } catch (err: any) {
            console.error(
              `[refresh] Failed to fetch P/L for ${row.role} ${row.id}:`,
              err.message || err,
            ); // Debug: Log error
            return { id: Number(row.id), profitLoss: 0 };
          } finally {
            setPlLoading((prev) => ({ ...prev, [Number(row.id)]: false }));
          }
        },
      );

      const plResults = await Promise.all(plPromises);
      const plMap = Object.fromEntries(
        plResults.map(({ id, profitLoss }) => [Number(id), Number(profitLoss)]),
      );
      console.log("[refresh] P/L map:", plMap); // Debug: Log final P/L map
      setPlData(plMap);

      // Fetch exposure data for all staff
      try {
        const staffIds = rows1.map((r: { id: any }) => r.id).join(',');
        const expRes = await apiFetch<any>(`/sportsbetting/exposure-bulk?staffIds=${staffIds}`);
        if (expRes?.data?.exposures) {
          setExposureData(expRes.data.exposures);
        } else if (expRes?.exposures) {
          setExposureData(expRes.exposures);
        }
        // Store total platform exposure
        const totalExp = expRes?.data?.totalPlatformExposure || expRes?.totalPlatformExposure || 0;
        setTotalPlatformExposure(totalExp);
      } catch (expErr: any) {
        console.error('[refresh] Failed to fetch exposure data:', expErr.message || expErr);
      }
    } catch (err: any) {
      setSnack({
        open: true,
        severity: "error",
        message: err.message || "Server error",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  /* derived ------------------------------------------------------------------------------ */
  const roleTotals = useMemo(() => {
    const tot: Record<string, number> = {};
    Object.keys(ROLE_HIERARCHY).forEach((r) => (tot[r] = 0));
    rows.forEach((r) => {
      tot[r.role] += r.balance;
    });
    return tot;
  }, [rows]);

  const myChildren = useMemo(
    () => rows.filter((r) => r.parent_id === myId),
    [rows, myId],
  );
  const manageable = useMemo(
    () => rows.filter((r) => ROLE_HIERARCHY[r.role].level > myLvl),
    [rows, myLvl],
  );
  const haveFunds = (amt: number) => {
    const balNum = Number(myBal); // make 100 % sure we compare numbers
    const amtNum = Number(amt);
    console.log("[guard] bal =", balNum, "amt =", amtNum); // ①

    if (Number.isNaN(balNum) || Number.isNaN(amtNum)) {
      console.warn("[guard] one of the values is NaN!");
      return false; // treat NaN as “no funds”
    }
    return balNum >= amtNum;
  };

  /* transfer ----------------------------------------------------------------------------- */
  const doTransfer = async (opts: {
    toId: number;
    toRole: Row["role"];
    amount: number;
    direction: "deposit" | "withdraw";
  }) => {
    const { toId, toRole, amount, direction } = opts;
    if (!amount || amount <= 0)
      return setSnack({
        open: true,
        severity: "error",
        message: "Enter valid amount",
      });
    console.log(direction, toRole, amount, toId);
    console.log("[action] direction =", direction); // ②
    if (direction === "deposit" && !haveFunds(amount)) {
      console.log("[action] blocked – insufficient"); // ③
      setSnack({
        open: true,
        severity: "error",
        message: "Insufficient balance",
      });
      return; // ← hard stop
    }
    if (direction === "withdraw") {
      const child = rows.find((r) => r.id === toId);
      if (!child) return;
      if (amount > child.balance)
        return setSnack({
          open: true,
          severity: "error",
          message: `${child.name} has only ₹${child.balance.toFixed(2)}`,
        });
    }

    const body = {
      toType: toRole === "User" ? "user" : "staff",
      toId,
      amount,
      direction,
    };

    try {
      await apiFetch("/api/staff/transfer", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setSnack({
        open: true,
        severity: "success",
        message: `${direction === "deposit" ? "Sent" : "Received"} ₹${amount}`,
      });
      setDepVal((v) => ({ ...v, [toId]: "" }));
      setWdVal((v) => ({ ...v, [toId]: "" }));
      refresh();
      window.dispatchEvent(new Event("balance-changed"));
    } catch (err: any) {
      setSnack({
        open: true,
        severity: "error",
        message: err.message || "Transfer failed",
      });
    }
  };

  /* loading guard */
  if (loading)
    return <div className="text-center py-16 text-gray-400">Loading…</div>;

  /* --- SEARCH/ FILTER LOGIC (applies both for SubAdmin and others) --- */
  const normalize = (s: string) => (s || "").toString().toLowerCase().trim();
  const matchesFilter = (r: Row) => {
    const qn = normalize(q);
    const roleN = normalize(roleFilter);
    const nameMatch = qn === "" || normalize(r.name).includes(qn);
    const roleMatch =
      roleN === "all" ||
      roleFilter === "All" ||
      normalize(r.role).includes(roleN);
    return nameMatch && roleMatch;
  };

  /* view for Sub-Admin only -------------------------------------------------------------- */
  if (myRole === "SubAdmin") {
    const childBal = myChildren.reduce((s, c) => s + c.balance, 0);
    const filteredChildren = myChildren.filter(matchesFilter);

    return (
      <Section>
        {/* search box */}
        <div className="flex items-center gap-2 mb-4">
          <input
            className="border border-[#3a3f40] bg-[#292d2e] text-white rounded px-2 py-1 w-60 placeholder-gray-500 focus:outline-none focus:border-[#25ee89]"
            placeholder="Search by username..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            className="border border-[#3a3f40] bg-[#292d2e] text-white rounded px-2 py-1 focus:outline-none"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option>All</option>
            {Object.keys(ROLE_HIERARCHY).map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <button
            className="px-3 py-1 bg-[#3a3f40] text-white rounded hover:bg-[#4a4f50]"
            onClick={() => {
              setQ("");
              setRoleFilter("All");
            }}
          >
            Clear
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <BalanceCard title="Your Balance" balance={myBal} />
          <BalanceCard title="Total Child Balance" balance={childBal} />
          <BalanceCard title="Available Operations" balance={myBal} />
        </div>

        <BankTable
          rows={filteredChildren}
          rollup={rollup}
          depVal={depVal}
          wdVal={wdVal}
          setDepVal={setDepVal}
          setWdVal={setWdVal}
          onTransfer={doTransfer}
          plData={plData}
          plLoading={plLoading}
          exposureData={exposureData}
          onExposureClick={openExposureModal}
        />

        <Snack
          {...snack}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        />
      </Section>
    );
  }

  /* everyone else ----------------------------------------------------------------------- */
  const visible =
    myRole === "SuperAdmin"
      ? rows.filter((r) => r.role !== "SuperAdmin")
      : manageable;
  const filteredVisible = visible.filter(matchesFilter);
  const isAdmin = myRole === "Admin";

  return (
    <Section>
      {/* search box */}
      <div className="flex items-center gap-2 mb-4">
        <input
          className="border border-[#3a3f40] bg-[#292d2e] text-white rounded px-2 py-1 w-60 placeholder-gray-500 focus:outline-none focus:border-[#25ee89]"
          placeholder="Search by username..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="border border-[#3a3f40] bg-[#292d2e] text-white rounded px-2 py-1 focus:outline-none"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option>All</option>
          {Object.keys(ROLE_HIERARCHY).map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
        <button
          className="px-3 py-1 bg-[#3a3f40] text-white rounded hover:bg-[#4a4f50]"
          onClick={() => {
            setQ("");
            setRoleFilter("All");
          }}
        >
          Clear
        </button>
      </div>

      {/* summary cards */}
      {isAdmin ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <BalanceCard title="Total User Balance" balance={roleTotals.User} />
          <BalanceCard title="Total Sub-Agent Balance" balance={roleTotals["Sub-Agent"] || 0} />
          <BalanceCard title="Total Agent Balance" balance={roleTotals.Agent} />
          <BalanceCard title="Total Master Balance" balance={roleTotals.Master || 0} />
          <BalanceCard title="Total Sub-Admin Balance" balance={roleTotals["Sub-Admin"] || 0} />
          <BalanceCard title="Total User Exposure" balance={totalPlatformExposure} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <BalanceCard title="Total User Balance" balance={roleTotals.User} />
            <BalanceCard
              title="Total Sub-Agent Balance"
              balance={roleTotals.SubAgent}
            />
            <BalanceCard
              title="Total Agent Balance"
              balance={roleTotals.Agent}
            />
            <BalanceCard
              title="Total Master Balance"
              balance={roleTotals.Master}
            />
            <BalanceCard
              title="Total Sub-Admin Balance"
              balance={roleTotals.SubAdmin}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <BalanceCard
              title="Total Admin Balance"
              balance={roleTotals.Admin}
            />
          </div>
        </>
      )}

      <BankTable
        rows={filteredVisible}
        rollup={rollup}
        depVal={depVal}
        wdVal={wdVal}
        setDepVal={setDepVal}
        setWdVal={setWdVal}
        onTransfer={doTransfer}
        plData={plData}
        plLoading={plLoading}
        exposureData={exposureData}
        onExposureClick={openExposureModal}
      />

      <Snack
        {...snack}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />

      {/* Exposure Bet Details Modal */}
      {expModal && expModalUser && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/70 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-[850px] max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Open Bets — {expModalUser.name}
                </h2>
                <p className="text-sm text-gray-500">User ID: {expModalUser.id} | Exposure: ₹{(exposureData[expModalUser.id] ?? 0).toFixed(2)}</p>
              </div>
              <button onClick={() => setExpModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              {expModalLoading ? (
                <div className="py-10 text-center text-gray-500">Loading...</div>
              ) : expModalBets.length === 0 ? (
                <div className="py-10 text-center text-gray-500">No open bets found</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Game / Match</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Stake</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expModalBets.map((bet: any, idx: number) => (
                      <tr key={bet.id || idx} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium">{bet.game_title === '大厅' ? 'LuckySport' : (bet.game_title || '-')}</div>
                          {bet.team_one && bet.team_two && (
                            <div className="text-xs text-gray-500">{bet.team_one} vs {bet.team_two}</div>
                          )}
                          {bet.category && <div className="text-xs text-gray-400">{bet.category}</div>}
                        </td>
                        <td className="px-3 py-2 text-sm">{bet.user_name || '-'}</td>
                        <td className="px-3 py-2 text-right font-medium">{parseFloat(bet.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">Pending</span>
                        </td>
                        <td className="px-3 py-2">{bet.currency_code || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {bet.transaction_timestamp ? new Date(bet.transaction_timestamp).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => setExpModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Section>
  );
}

/* ────────────────────────── PRESENTATIONAL SUB-COMPONENTS ───────────── */

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="p-2 max-w-7xl mx-auto">{children}</div>
);

const Snack: React.FC<SnackbarState & { onClose: () => void }> = ({
  open,
  severity,
  message,
  onClose,
}) => (
  <Snackbar open={open} autoHideDuration={3000} onClose={onClose}>
    <Alert severity={severity} onClose={onClose}>
      {message}
    </Alert>
  </Snackbar>
);

/* table (layout unchanged) ------------------------------------------- */
interface TableProps {
  rows: Row[];
  rollup: Record<number, any>;
  depVal: Record<number, string>;
  wdVal: Record<number, string>;
  setDepVal: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  setWdVal: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  onTransfer: (o: {
    toId: number;
    toRole: Row["role"];
    amount: number;
    direction: "deposit" | "withdraw";
  }) => void;
  plData: Record<number, number>;
  plLoading?: Record<number, boolean>;
  exposureData?: Record<number, number>;
  onExposureClick?: (user: {id: number, name: string}) => void;
}

const BankTable: React.FC<TableProps> = ({
  rows,
  rollup,
  depVal,
  wdVal,
  setDepVal,
  setWdVal,
  onTransfer,
  plData,
  plLoading = {},
  exposureData = {},
  onExposureClick,
}) => (
  <div className="bg-[#232626] rounded-xl shadow-sm border border-[#2f3536] overflow-x-auto">
    <table className="w-full">
      <thead className="bg-[#1a1d1e]">
        {[
          "USERNAME",
          "ROLE",
          "AVAILABLE BALANCE",
          "BALANCE IN DL",
          "EXPOSURE IN DL",
          "DEPOSIT",
          "WITHDRAW",
          "CREDIT REF.",
          "REF P/L",
          "REMARK",
          "ALL",
        ].map((h, i) => (
          <th
            key={i}
            className="border border-[#3a3f40] px-2 py-2 text-xs font-bold text-gray-300 uppercase"
          >
            {h}
          </th>
        ))}
      </thead>
      <tbody className="divide-y">
        {rows.map((r) => {
          const dep = Number(depVal[r.id] || 0);
          const wdr = Number(wdVal[r.id] || 0);

          return (
            <tr key={r.id} className="hover:bg-[#292d2e] text-xs">
              <td className="border border-[#3a3f40] px-2 py-2 underline text-[#25ee89]">
                {r.name}
              </td>
              <td className="border border-[#3a3f40] px-2 py-2 font-semibold text-white">
                {r.role}
              </td>
              <td className="border border-[#3a3f40] px-2 py-2 font-mono text-white">
                {r.balance.toFixed(2)}
              </td>
              <td className="border border-[#3a3f40] px-2 py-2 text-white">
                {(rollup[r.id]?.downline_balance ?? 0).toFixed(2)}
              </td>
              <td className="border border-[#3a3f40] px-2 py-2 text-white">
                <button
                  onClick={() => onExposureClick?.({ id: r.id, name: r.name })}
                  className="underline text-[#25ee89] hover:text-white cursor-pointer"
                >
                  {(exposureData[r.id] ?? 0).toFixed(2)}
                </button>
              </td>

              {/* deposit */}
              <td className="border border-[#3a3f40] px-2 py-2">
                <input
                  type="number"
                  min={0}
                  value={depVal[r.id] || ""}
                  onChange={(e) =>
                    setDepVal((v) => ({ ...v, [r.id]: e.target.value }))
                  }
                  className="w-16 border border-[#3a3f40] bg-[#1a1d1e] text-white rounded px-1 mr-1"
                />
                <button
                  className="px-2 py-1 rounded text-black bg-[#25ee89] hover:bg-[#1fcc76]"
                  disabled={!dep}
                  onClick={() =>
                    onTransfer({
                      toId: r.id,
                      toRole: r.role,
                      amount: dep,
                      direction: "deposit",
                    })
                  }
                >
                  Deposit
                </button>
              </td>

              {/* withdraw */}
              <td className="border border-[#3a3f40] px-2 py-2">
                <input
                  type="number"
                  min={0}
                  value={wdVal[r.id] || ""}
                  onChange={(e) =>
                    setWdVal((v) => ({ ...v, [r.id]: e.target.value }))
                  }
                  className="w-16 border border-[#3a3f40] bg-[#1a1d1e] text-white rounded px-1 mr-1"
                />
                <button
                  className="px-2 py-1 rounded text-white bg-red-600 hover:bg-red-700"
                  disabled={!wdr}
                  onClick={() =>
                    onTransfer({
                      toId: r.id,
                      toRole: r.role,
                      amount: wdr,
                      direction: "withdraw",
                    })
                  }
                >
                  Withdraw
                </button>
              </td>

              {/* untouched cols */}
              <td className="border border-[#3a3f40] px-2 py-2" />
              <td className="border border-[#3a3f40] px-2 py-2 font-mono text-white">
                {plLoading[r.id]
                  ? "Loading..."
                  : plData[r.id] !== undefined
                    ? plData[r.id].toFixed(2)
                    : "N/A"}
              </td>
              <td className="border border-[#3a3f40] px-2 py-2" />
              <td className="border border-[#3a3f40] px-2 py-2" />
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
